import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiMapPinLine,
  RiMapPin2Fill,
  RiBuilding2Line,
  RiUserLine,
  RiKeyLine,
  RiSettings4Line,
  RiContactsLine,
  RiStarFill,
  RiHistoryLine,
  RiFileList3Line,
  RiToolsLine,
  RiTruckLine,
  RiDeleteBinLine,
  RiMailLine,
  RiComputerLine,
  RiLoader4Line,
  RiPulseLine,
} from 'react-icons/ri';
import siteService, { Site, ContactTypeLabels, SiteCurrentEquipmentItem } from '../../services/siteService';
import rentalService, { Rental } from '../../services/rentalService';
import deviceMaintenanceService, { DeviceMaintenance } from '../../services/deviceMaintenanceService';
import equipmentReturnService from '../../services/equipmentReturnService';
import type { ReturnItem } from '../../types/equipment-return';
import { RETURN_STATUS_LABELS } from '../../types/equipment-return';
import { useDeleteSite } from '../../hooks/mutations/useSiteMutations';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { SITES_PATH, siteEditUrl } from '../../utils/siteLinks';
import { companyDetailUrl } from '../../utils/companyLinks';
import { deviceDetailUrl } from '../../utils/deviceLinks';
import { returnRequestUrl } from '../../utils/returnCancellationLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert, showConfirm } from '../../utils/alert';
import { formatDateTimeFull, formatDate } from '../../utils/dateUtils';
import { formatActivationCode } from '../../utils/activationCode';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { describeReturnItem } from '../../utils/returnItemLabel';
import StatusBadge from '../../components/maintenance/StatusBadge';
import SiteAppCredentialsSection from '../../components/sites/SiteAppCredentialsSection';
import { SiteAccessoryIssueHistoryCard, SiteReplacementHistoryCard } from '../../components/sites/SiteHistorySections';
import { siteToForm, siteToContacts, SITE_FIELD_LABELS, SITE_STATUS_LABEL } from '../../components/sites/siteForm';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  RelatedRecordsCard,
  useDetailNavigation,
  detailNavState,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

const LIST_PATH = SITES_PATH;
const RELATED_LIMIT = 10;

const yesNo = (v: unknown) => (v ? 'เปิดใช้งาน' : 'ปิด');

/**
 * อุปกรณ์/ซิมที่อยู่ที่โครงการนี้ตอนนี้ — GET /sites/{id}/current-equipment (sites.view = สิทธิ์ของหน้านี้อยู่แล้ว)
 * ลิงก์สัญญา gate rentals.view · ลิงก์เครื่อง gate devices.view (G-12: ลิงก์ข้ามโมดูลต้องมีสิทธิ์ปลายทาง)
 * AnyDesk มาจาก rental_items เท่านั้น (backend) · ชิ้นที่รับคืนก่อนสัญญาจบ = ป้าย "รับคืนแล้ว" (ไม่ซ่อน)
 */
const SiteCurrentEquipmentCard: React.FC<{ siteId: number; hereState: unknown }> = ({ siteId, hereState }) => {
  const { hasPermission } = usePermission();
  const canRentals = hasPermission('rentals.view');
  const canDevices = hasPermission('devices.view');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['site-related', siteId, 'current-equipment'],
    queryFn: () => siteService.getCurrentEquipment(siteId),
    staleTime: 30 * 1000,
  });
  const items = data?.items ?? [];

  const renderItem = (it: SiteCurrentEquipmentItem) => {
    const st = getRentalStatusDisplay(it.rental_status);
    return (
      <li key={it.rental_item_id} className="px-4 py-3 sm:px-6 min-w-0" data-testid="site-equipment-row">
        <div className="flex flex-wrap items-start justify-between gap-2 min-w-0">
          <div className="min-w-0 space-y-1">
            {it.device && (
              <p className="text-sm text-secondary-900 [overflow-wrap:anywhere]">
                {canDevices ? (
                  <Link to={deviceDetailUrl(it.device.id)} state={hereState} className="font-mono font-medium text-primary-700 hover:text-primary-900">
                    {it.device.code || `#${it.device.id}`}
                  </Link>
                ) : (
                  <span className="font-mono font-medium">{it.device.code || `#${it.device.id}`}</span>
                )}
                {it.device.name && <span className="ml-1.5">{it.device.name}</span>}
                {!it.device.is_serialized && it.quantity ? <span className="ml-1.5 text-secondary-500">× {it.quantity}</span> : null}
                {it.device.serial_number && <span className="block text-xs text-secondary-500 font-mono">S/N {it.device.serial_number}</span>}
                {it.device_returned && (
                  <span className="ml-0 mt-0.5 inline-block px-2 py-0.5 rounded-full text-xs bg-warning-100 text-warning-800">🔒 รับคืนแล้ว · สัญญายังไม่จบ</span>
                )}
              </p>
            )}
            {it.sim && (
              <p className="text-sm text-secondary-700 [overflow-wrap:anywhere]">
                <span className="text-secondary-500">ซิม</span> <span className="font-mono">{it.sim.phone_number || `#${it.sim.id}`}</span>
                {it.sim.operator && <span className="ml-1 text-secondary-500">({it.sim.operator})</span>}
                {it.sim_returned && (
                  <span className="ml-1.5 inline-block px-2 py-0.5 rounded-full text-xs bg-warning-100 text-warning-800">🔒 รับคืนแล้ว · สัญญายังไม่จบ</span>
                )}
              </p>
            )}
            {it.anydesk_id && (
              <p className="text-xs text-secondary-600">
                <RiComputerLine className="inline h-3.5 w-3.5 mr-1" />AnyDesk <span className="font-mono">{it.anydesk_id}</span>
              </p>
            )}
            {it.product_name && <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">{it.product_name}</p>}
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0 text-right">
            {canRentals ? (
              <Link to={`/rentals/view/${it.rental_id}`} state={hereState} className="text-sm font-mono text-primary-700 hover:text-primary-900">
                {it.rental_number || `#${it.rental_id}`}
              </Link>
            ) : (
              <span className="text-sm font-mono text-secondary-700">{it.rental_number || `#${it.rental_id}`}</span>
            )}
            <span className={cn('px-2 py-0.5 rounded-full text-xs', st.badgeColor)}>{st.label}</span>
            {!it.delivery_date && <span className="text-xs text-warning-700">ยังไม่ส่งมอบ</span>}
          </div>
        </div>
      </li>
    );
  };

  return (
    <DetailSection
      title={items.length > 0 ? `อุปกรณ์ที่อยู่ที่โครงการนี้ตอนนี้ (${items.length.toLocaleString()})` : 'อุปกรณ์ที่อยู่ที่โครงการนี้ตอนนี้'}
      icon={<RiComputerLine />}
      padded={false}
      testId="site-current-equipment"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-secondary-500">
          <RiLoader4Line className="h-5 w-5 animate-spin mr-2 text-primary-500" />
          กำลังโหลด...
        </div>
      ) : isError ? (
        <div className="px-4 py-4 sm:px-6 text-sm text-danger-700">
          {getApiErrorMessage(error, 'โหลดข้อมูลไม่สำเร็จ')}{' '}
          <button type="button" onClick={() => refetch()} className="underline">ลองอีกครั้ง</button>
        </div>
      ) : items.length === 0 ? (
        <p className="px-4 py-6 sm:px-6 text-sm text-secondary-500 text-center">
          ไม่มีอุปกรณ์จากสัญญาเช่าที่ยังใช้งานอยู่ที่โครงการนี้
        </p>
      ) : (
        <ul className="divide-y divide-secondary-100">{items.map(renderItem)}</ul>
      )}
    </DetailSection>
  );
};

/**
 * หน้ารายละเอียดโครงการ — `/master-data/sites/:id` (แทนโมดัล "รายละเอียดโครงการ" · มติ PO 28 ต.ค. 2026)
 * ข้อมูลครบทุกช่องของฟอร์มเดิม (อ่านอย่างเดียว) + การใช้งาน (การเช่า/สัญญา/ร่าง — ข้อมูลโครงการว่าง site_idle) +
 * การ์ดที่เกี่ยวข้อง (อุปกรณ์ตอนนี้ · สัญญาเช่า · เคสซ่อม · ใบตามเก็บ — gate สิทธิ์ปลายทางแยกใบ) +
 * ข้อมูลการสร้าง/แก้ไข + ประวัติกิจกรรม
 */
const SiteViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const siteId = parseRouteId(id);
  const location = useLocation();
  const { hasPermission } = usePermission();
  const { backTo, goTo, navState } = useDetailNavigation(LIST_PATH);
  const deleteSiteMutation = useDeleteSite();
  const [activityRefresh, setActivityRefresh] = useState(0);

  const { data: site, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.sites.detail(siteId ?? 0),
    queryFn: () => siteService.getSite(siteId!),
    enabled: siteId !== null,
  });

  // ลิงก์จากการ์ดที่เกี่ยวข้องไปหน้ารายละเอียดของมัน แล้วกด "กลับ" ต้องกลับมาหน้าโครงการนี้
  const hereState = detailNavState(location);

  const handleRefresh = () => {
    refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleDelete = (s: Site) => {
    showConfirm(
      'ยืนยันการลบโครงการ',
      `คุณต้องการลบโครงการ ${s.name} ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้`,
      async () => {
        try {
          const result = await deleteSiteMutation.mutateAsync(s.id);
          if (result && result.success === false) {
            showAlert('ไม่สามารถลบโครงการ', result.message || 'ไม่สามารถลบโครงการได้', 'warning');
            return;
          }
          goTo(backTo, { replace: true });
        } catch (e) {
          // useMutationWithRefresh แสดงข้อความจาก server แล้ว (เช่น ยังมีการเช่าผูกอยู่)
          console.error('Error deleting site:', e);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  const notFound = siteId === null ? { response: { status: 404 } } : null;
  const form = site ? siteToForm(site) : null;
  const contacts = site ? siteToContacts(site) : [];
  const isActive = form?.status === 'active';
  const companyName = site?.company?.name || site?.company_name;
  // customer_code = legacy ที่ K-Visit ใช้จับคู่ — โชว์แยกเมื่อไม่ใช่ค่าเดียวกับที่ช่อง "รหัสลูกค้า" แสดงอยู่แล้ว
  const legacyCustomerCode = site?.customer_code && site.customer_code !== form?.customer_number ? site.customer_code : null;

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'ข้อมูลโครงการ', path: backTo }, { label: site?.name || 'รายละเอียด' }]}
      icon={<RiMapPinLine />}
      title={site?.name || 'รายละเอียดโครงการ'}
      recordId={site?.code}
      subtitle={companyName ? `โครงการของ ${companyName}` : 'รายละเอียดโครงการ'}
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={site ? siteEditUrl(site.id) : undefined}
      editPermission="sites.edit"
      editState={navState}
      actions={
        site && hasPermission('sites.delete') ? (
          <button type="button" onClick={() => handleDelete(site)} className={cn(headerButtonClass, headerButtonVariants.danger)}>
            <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
            ลบ
          </button>
        ) : undefined
      }
      loading={siteId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบโครงการที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        site && (
          <>
            <RecordMetaCard
              createdAt={site.created_at}
              createdBy={site.created_by}
              // แสดงการแก้ไขเฉพาะเมื่อเคยถูกแก้ไขจริง (เหมือนโมดัลเดิม)
              updatedAt={site.updated_by ? site.updated_at : undefined}
              updatedBy={site.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="site"
                entityId={site.id}
                refreshTrigger={activityRefresh}
                fieldLabels={SITE_FIELD_LABELS}
                valueFormatters={{
                  status: (v) => SITE_STATUS_LABEL(String(v)),
                  k_connect_enabled: yesNo,
                  telegram_enabled: yesNo,
                }}
              />
            </DetailSection>
          </>
        )
      }
    >
      {site && form && (
        <>
          <DetailSection
            title="ข้อมูลโครงการ"
            icon={<RiMapPinLine />}
            actions={
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                  isActive ? 'bg-success-50 text-success-700 border-success-200' : 'bg-danger-50 text-danger-700 border-danger-200'
                )}
                data-testid="site-status-badge"
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', isActive ? 'bg-success-500' : 'bg-danger-500')} />
                {SITE_STATUS_LABEL(form.status)}
              </span>
            }
          >
            <InfoGrid>
              <InfoField label="รหัสโครงการ" value={form.code} mono />
              <InfoField label="ชื่อโครงการ" value={form.name} />
              <InfoField label="ชื่อใช้งานจริงที่ Activate ในระบบ" value={form.display_name} fullWidth />
              <InfoField label="ชื่อที่ใช้ในแอพ" value={form.app_name} />
              <InfoField label="บริษัท" value={companyName} testId="site-company-field">
                {companyName && hasPermission('companies.view') ? (
                  <Link
                    to={companyDetailUrl(site.company_id)}
                    state={hereState}
                    className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 [overflow-wrap:anywhere]"
                  >
                    <RiBuilding2Line className="h-4 w-4 flex-shrink-0" />
                    {companyName}
                  </Link>
                ) : undefined}
              </InfoField>
            </InfoGrid>
          </DetailSection>

          {/* การใช้งานของโครงการ (Phase 2 สถานะโครงการ · แจ้งเตือน site_idle พามาหน้านี้) — ช่วยตัดสินใจก่อนปิดโครงการ */}
          <DetailSection title="การใช้งาน" icon={<RiPulseLine />} testId="site-usage">
            <InfoGrid columns={3}>
              <InfoField label="การเช่าที่ใช้งานอยู่" value={`${(site.active_rental_count || 0).toLocaleString()} รายการ`} />
              <InfoField label="สัญญาที่ใช้งานอยู่" value={`${(site.active_contract_count || 0).toLocaleString()} ฉบับ`} />
              <InfoField label="ร่างการเช่า" value={`${(site.draft_rental_count || 0).toLocaleString()} รายการ`} />
            </InfoGrid>
            {(site.active_rental_count || 0) === 0 && (
              <p className="mt-3 text-sm text-secondary-600" data-testid="site-idle-note">
                {(site.draft_rental_count || 0) > 0
                  ? `ว่าง · มีร่างการเช่า ${site.draft_rental_count} รายการ — ระบบยังถือว่าโครงการใช้งานอยู่`
                  : 'ว่าง — ไม่มีการเช่าที่ใช้งานอยู่'}
              </p>
            )}
          </DetailSection>

          <DetailSection title="ที่อยู่และรหัส Activate" icon={<RiMapPin2Fill />}>
            <InfoGrid>
              <InfoField label="ที่อยู่" value={form.address} multiline fullWidth />
              <InfoField label="รหัสลูกค้า" value={formatActivationCode(form.customer_number) || form.customer_number} mono />
              <InfoField label="รหัส Activate" value={formatActivationCode(form.activation_code) || form.activation_code} mono />
              {legacyCustomerCode && (
                <InfoField label="รหัสลูกค้า (เดิม)" value={legacyCustomerCode} mono hint="ค่าเดิมที่ K-Visit ใช้จับคู่โครงการ" />
              )}
            </InfoGrid>
          </DetailSection>

          {/* บัญชีเข้าระบบแอพ — endpoint แยก /sites/{id}/app-credentials · ดูรหัสจริงต้องมี sites.app_credentials */}
          <DetailSection title="บัญชีเข้าระบบแอพ" icon={<RiKeyLine />}>
            <SiteAppCredentialsSection siteId={site.id} mode="view" canManage={hasPermission('sites.app_credentials')} hideHeader />
          </DetailSection>

          <DetailSection title="ข้อมูลผู้ติดต่อ" icon={<RiUserLine />}>
            <InfoGrid>
              <InfoField label="ผู้ติดต่อ" value={form.contactPerson} />
              <InfoField label="เบอร์โทรศัพท์" value={form.phone} mono />
              <InfoField label="อีเมล" value={form.email}>
                {form.email ? (
                  <a href={`mailto:${form.email}`} className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 [overflow-wrap:anywhere]">
                    <RiMailLine className="h-4 w-4 flex-shrink-0" />
                    {form.email}
                  </a>
                ) : undefined}
              </InfoField>
            </InfoGrid>
          </DetailSection>

          <DetailSection title="การตั้งค่าและเปิดใช้งาน" icon={<RiSettings4Line />}>
            <InfoGrid>
              <InfoField label="K-Connect" value={yesNo(form.k_connect_enabled)} testId="site-kconnect" />
              <InfoField label="Telegram" value={yesNo(form.telegram_enabled)} testId="site-telegram" />
              <InfoField label="เบอร์โทรโครงการ/นิติบุคคล" value={form.project_phone} fullWidth />
              <InfoField label="ข้อมูลติดต่อ รปภ./ผู้ดูแล" value={form.contact_notes} multiline fullWidth />
            </InfoGrid>
          </DetailSection>

          <DetailSection
            title={contacts.length > 0 ? `รายชื่อผู้ติดต่อโครงการ (${contacts.length})` : 'รายชื่อผู้ติดต่อโครงการ'}
            icon={<RiContactsLine />}
            padded={false}
            testId="site-contacts"
          >
            {contacts.length === 0 ? (
              <p className="px-4 py-6 sm:px-6 text-sm text-secondary-500 text-center">ยังไม่มีรายชื่อผู้ติดต่อ</p>
            ) : (
              <ul className="divide-y divide-secondary-100">
                {contacts.map((c, i) => (
                  <li key={i} className="px-4 py-3 sm:px-6 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-600 rounded">
                        {ContactTypeLabels[c.contact_type] || c.contact_type}
                      </span>
                      <span className="text-sm font-medium text-secondary-900 [overflow-wrap:anywhere]">{c.name || '-'}</span>
                      {c.is_primary && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-warning-100 text-warning-700 rounded">
                          <RiStarFill className="w-3 h-3 mr-1" />
                          ผู้ติดต่อหลัก
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-secondary-600 space-x-3 [overflow-wrap:anywhere]">
                      {c.phone && <span className="font-mono">{c.phone}</span>}
                      {c.line_id && <span>Line: {c.line_id}</span>}
                    </div>
                    {c.notes && <p className="mt-0.5 text-xs text-secondary-500 [overflow-wrap:anywhere]">{c.notes}</p>}
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          <SiteCurrentEquipmentCard siteId={site.id} hereState={hereState} />

          {/* TODO(แท็บอนาคต): "ประวัติอุปกรณ์ของโครงการ" — อุปกรณ์/ซิมที่เคยอยู่โครงการนี้ทั้งหมด (สัญญาจบแล้ว ·
              เปลี่ยนเครื่อง · ย้ายข้ามโครงการ) จะวางตรงนี้ ถัดจากการ์ด "อุปกรณ์ที่อยู่ที่โครงการนี้ตอนนี้"
              ยังไม่ทำ (มติ PO 28 ต.ค. 2026 — เว้นที่ไว้เท่านั้น) */}

          <RelatedRecordsCard<Rental>
            title="สัญญาเช่าของโครงการนี้"
            icon={<RiFileList3Line />}
            permission="rentals.view"
            queryKey={['site-related', site.id, 'rentals']}
            fetcher={() => rentalService.getRentals({ site_id: site.id, limit: RELATED_LIMIT })}
            rowKey={(r) => r.id}
            rowTo={(r) => `/rentals/view/${r.id}`}
            rowState={hereState}
            emptyText="โครงการนี้ยังไม่มีรายการเช่า"
            testId="site-related-rentals"
            renderRow={(r) => {
              const st = getRentalStatusDisplay(r.status);
              return (
                <>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900 font-mono">{r.rental_number}</p>
                    <p className="text-xs text-secondary-500">
                      {r.rental_date ? formatDate(r.rental_date) : '-'}
                      {r.expected_return_date ? ` – ${formatDate(r.expected_return_date)}` : ''}
                    </p>
                  </div>
                  <span className={cn('px-2 py-0.5 rounded-full text-xs flex-shrink-0', st.badgeColor)}>{st.label}</span>
                </>
              );
            }}
          />

          <RelatedRecordsCard<DeviceMaintenance>
            title="เคสซ่อมบำรุงของโครงการนี้"
            icon={<RiToolsLine />}
            permission="maintenance.view"
            queryKey={['site-related', site.id, 'maintenance']}
            fetcher={() => deviceMaintenanceService.getMaintenanceRecords({ site_id: site.id, limit: RELATED_LIMIT })}
            rowKey={(m) => m.id}
            rowTo={(m) => `/maintenance/view/${m.id}`}
            rowState={hereState}
            // closed=1: การ์ดนับทุกสถานะ — หน้ารายการตั้งต้น "เฉพาะเคสที่ยังไม่ปิด" จะขึ้นน้อยกว่าที่การ์ดบอก (E2E R1: การ์ด 20 → รายการ 1)
            viewAllTo={`/maintenance/list?site_id=${site.id}&closed=1`}
            emptyText="โครงการนี้ยังไม่มีเคสซ่อมบำรุง"
            testId="site-related-maintenance"
            renderRow={(m) => (
              <>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary-900">
                    <span className="font-mono">#{m.id}</span>
                    {m.maintenance_type?.name && <span className="ml-1.5">{m.maintenance_type.name}</span>}
                  </p>
                  <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
                    {m.maintenance_date ? formatDateTimeFull(m.maintenance_date) : '-'}
                    {m.device?.code ? ` · ${m.device.code}` : ''}
                  </p>
                </div>
                <StatusBadge status={m.status || 'OPEN'} className="flex-shrink-0" />
              </>
            )}
          />

          {/* FB-2026-0009 (tirawat โครงการ #682): ประวัติไว้ตอบคำถามบริษัท รปภ. — เปลี่ยนเครื่อง/ซิม + เบิก/เปลี่ยนอุปกรณ์เสริม
              (ประวัติซ่อม = การ์ดเคสซ่อมด้านบน) · gate สิทธิ์ของโมดูลต้นทางแยกการ์ด */}
          <SiteReplacementHistoryCard siteId={site.id} hereState={hereState} />
          <SiteAccessoryIssueHistoryCard siteId={site.id} hereState={hereState} />

          <RelatedRecordsCard<ReturnItem>
            title="ใบตามเก็บอุปกรณ์ของโครงการนี้"
            icon={<RiTruckLine />}
            permission="equipment_returns.view"
            queryKey={['site-related', site.id, 'equipment-returns']}
            fetcher={() => equipmentReturnService.getItems({ site_id: site.id, limit: RELATED_LIMIT })}
            rowKey={(it) => it.id}
            // หน้าใบตามเก็บเต็ม (767233c4) — เปิดใบของชิ้นนั้นพร้อมไฮไลต์ ?item=
            rowTo={(it) => returnRequestUrl(it.return_request_id, it.id)}
            rowState={hereState}
            emptyText="โครงการนี้ไม่มีใบตามเก็บอุปกรณ์"
            testId="site-related-returns"
            renderRow={(it) => {
              const d = describeReturnItem(it);
              return (
                <>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900 [overflow-wrap:anywhere]">
                      <span className="text-secondary-500 font-normal">{d.kindLabel}</span> {d.primary}
                    </p>
                    <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
                      {it.return_request?.rental?.rental_number || '-'}
                      {it.expected_return_date ? ` · นัดคืน ${formatDate(it.expected_return_date)}` : ''}
                    </p>
                  </div>
                  <span className="text-xs text-secondary-700 flex-shrink-0">{RETURN_STATUS_LABELS[it.status] || it.status}</span>
                </>
              );
            }}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export default SiteViewPage;
